import ImportantAcknowledgement from "./ImportantAcknowledgement";
import {useState} from "react";
import {Button, Card, Col, Collapse, Container, Row} from "react-bootstrap";
import {determinBorder, determinColor, getDisplayStatus} from "../service/jobutil";
import {CopyAddress} from "./CopyAddress";
import {SCHEDULER_BACKLOG_ZONE, setSchedulerDragData} from "./scheduler/schedulerDragDrop";
import BlockedAgencyIcon, {BLOCKED_AGENCY_MESSAGE} from "./scheduler/BlockedAgencyIcon";
import UpdateButtonMenu from "./UpdateJobButtonMenu";

function buildTaskTitleParts(job, sameDaySchedule) {
    const hasSameDayJob = Boolean(sameDaySchedule?.hasSameDayJob);
    const resolvedCheckOut = hasSameDayJob ? sameDaySchedule?.checkOut : job.check_out;
    const resolvedCheckIn = hasSameDayJob ? sameDaySchedule?.checkIn : job.check_in;

    return {
        leadingTitle: [job.full_address, job.key_number].filter(Boolean).join(", "),
        resolvedCheckOut,
        resolvedCheckIn,
    };
}

function buildTaskTagStyle(color) {
    return {
        display: "inline-block",
        backgroundColor: "#fff",
        color,
        border: `1.5px solid ${color}`,
        borderRadius: "4px",
        padding: "2px 8px",
        fontSize: "0.85em",
        fontWeight: 600,
        marginLeft: "8px",
        marginRight: "4px",
        verticalAlign: "middle",
    };
}

export default function TaskCardList({
    job,
    fieldWorkerMap,
    isLoading,
    onEditJob,
    onOpenWorkItems,
    onJobUpdated,
    defaultOpen = false,
    sameDayAssignmentLabel = "",
    sameDaySchedule = null,
    dragSourceZone,
    isAgencyBlocked = false,
    mobileAction = null,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const defaultTagColor = determinColor(job);
    const showSameDayB2BTag = Boolean(sameDaySchedule?.checkOut || sameDaySchedule?.checkIn);
    const isBlockedBacklogCard = isAgencyBlocked && dragSourceZone === SCHEDULER_BACKLOG_ZONE;
    const isDragDisabled = isLoading || isBlockedBacklogCard;
    const {leadingTitle, resolvedCheckOut, resolvedCheckIn} = buildTaskTitleParts(job, sameDaySchedule);

    return (
        <div style={{marginBottom: "3px"}}>
            <Container fluid>
                <Row>
                    <Col>
                        <Card
                            style={{
                                textAlign: "left",
                                userSelect: "text",
                                cursor: isBlockedBacklogCard ? "not-allowed" : undefined,
                            }}
                            draggable={!isDragDisabled}
                            title={isBlockedBacklogCard ? BLOCKED_AGENCY_MESSAGE : undefined}
                            onDragStart={(event) => setSchedulerDragData(event, job.cos_job_id, dragSourceZone)}
                            border={determinBorder(job)}
                        >
                            <Card.Body className="task-card-body">
                                <div onClick={() => setOpen(!open)}>
                                    <div style={{display: "flex"}}>
                                        <span>
                                            <b data-testid="task-card-title">
                                                {leadingTitle}
                                                {leadingTitle && job.agent_name ? ", " : ""}
                                                {job.agent_name || ""}
                                                {isBlockedBacklogCard ? <BlockedAgencyIcon /> : null}
                                                {resolvedCheckOut ? `, CheckOut:${resolvedCheckOut}` : ""}
                                                {resolvedCheckIn ? `, CheckIn:${resolvedCheckIn}` : ""}
                                            </b>
                                            {job.tags && job.tags.includes("B2B_NEW") ? (
                                                <span style={buildTaskTagStyle("red")}>
                                                    B2B_NEW
                                                </span>
                                            ) : showSameDayB2BTag ? (
                                                <span style={buildTaskTagStyle(defaultTagColor)}>
                                                    B2B
                                                </span>
                                            ) : null}
                                            <CopyAddress job={job} />
                                        </span>
                                        <span style={{marginLeft: "auto", color: determinColor(job)}}>
                                            <b>{getDisplayStatus(job)}</b><ImportantAcknowledgement job={job} onUpdated={onJobUpdated} disabled={isLoading}/>
                                        </span>
                                    </div>
                                    <div style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
                                        <span style={{flex: 1, color: "#0d6efd", display: "inline-flex", alignItems: "center", flexWrap: "wrap"}}>
                                            <b>{job.task_subtype === 'DEEP_CLEAN' ? 'Deep clean' : 'General Task'} ID:</b> {job.cos_job_id}
                                            {sameDayAssignmentLabel ? (
                                                <span style={buildTaskTagStyle("#0d6efd")}>
                                                    {sameDayAssignmentLabel}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="task-card-date" style={{flex: 1, textAlign: "right"}}>
                                            <span className="task-card-date-value">Job Date: {job.date || "—"}</span>
                                            {mobileAction ? (
                                                <span className="scheduler-mobile-inline-action">
                                                    {mobileAction}
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                    <div className="task-card-metadata" data-testid="task-card-metadata">
                                        <span>Created Date: {job.created_at || "—"}</span>
                                        <span>Property Next Job Date: {job.property_next_job_date || "No Job"}</span>
                                        <span className="task-card-worker">
                                            <b>Worker: </b>
                                            {fieldWorkerMap[job.cos_job_worker] || ""}
                                        </span>
                                    </div>
                                </div>
                                <Collapse in={open}>
                                    <div>
                                        <div style={{marginTop: "20px"}}>
                                            <span>{job.description}</span>
                                        </div>
                                        <div style={{display: "flex", marginTop: "20px"}}>
                                            <span>
                                                {job.email}<br />
                                                {job.phone}
                                            </span>
                                            <span style={{marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px"}}>
                                                {onOpenWorkItems ? <Button variant="link" className="p-0" disabled={isLoading}
                                                    onClick={event => {event.stopPropagation(); onOpenWorkItems();}}>Work items</Button> : null}
                                                <UpdateButtonMenu row={job} onClick={onEditJob} />
                                            </span>
                                        </div>
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
